import React from 'react';
import PromptTagEditor from '../components/PromptTagEditor';
import PromptTagReferenceLabel from '../components/PromptTagReferenceLabel';

const MainAnalysisConfigurator = ({
  title,
  onSave,
  onDiscard,
  highlightTemplatePicker = false,
  isCollapsed,
  onToggleCollapsed,
  saveDisabled,
  discardDisabled,
  setupSaveState,
  setupData,
  isSurveyType,
  loadingSetup,
  setupError,
  generateState,
  promptMissingQuestionsTag,
  questionsTag,
  onOpenTemplatePicker,
  templatePickerLoading,
  hasSelectedScript,
  mainPromptTagReferences,
  promptDraft,
  setPromptDraft,
  mainPromptKnownTags,
  knownPromptTagPatterns,
  questionsDraft,
  setQuestionsDraft,
  onGenerateQuestions,
}) => {
  const templatePickerDisabled = !hasSelectedScript || loadingSetup || templatePickerLoading;
  const shouldAnimateTemplatePicker = highlightTemplatePicker && !templatePickerDisabled;

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saveDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  saveDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {setupSaveState === 'saving' ? 'Saving…' : 'Save Changes'}
              </button>

              <button
                type="button"
                onClick={onDiscard}
                disabled={discardDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  discardDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                Discard
              </button>
            </>
          )}

          <button
            type="button"
            onClick={onToggleCollapsed}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            {isCollapsed ? 'Open' : 'Close'}
          </button>
        </div>
      </div>

      {!isCollapsed && (setupData.missingPrompt || (isSurveyType && setupData.missingQuestions)) && !loadingSetup && (
        <div className="text-sm text-amber-600">
          {isSurveyType && setupData.missingPrompt && setupData.missingQuestions
            ? 'No prompt or questions saved yet. Start from a blank slate and save.'
            : setupData.missingPrompt
              ? 'No prompt saved yet. Start from a blank slate and save.'
              : isSurveyType
                ? 'No questions saved yet. Start from a blank slate and save.'
                : ''}
        </div>
      )}

      {!isCollapsed && setupError && (
        <div className="text-sm text-red-600">{setupError}</div>
      )}

      {!isCollapsed && setupSaveState === 'saved' && (
        <div className="text-sm text-emerald-600">Saved successfully.</div>
      )}

      {!isCollapsed && isSurveyType && generateState === 'generated' && (
        <div className="text-sm text-emerald-600">Generated successfully.</div>
      )}

      {!isCollapsed && isSurveyType && promptMissingQuestionsTag && (
        <div className="text-sm text-amber-600">
          Prompt is missing the {questionsTag} tag. This tag is required to inject questions at runtime.
        </div>
      )}

      {!isCollapsed && (
        <div className={`grid gap-4 ${isSurveyType ? 'lg:grid-cols-2' : ''}`}>
        <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Prompt Template</h3>
            <div className="flex items-center gap-2">
              {loadingSetup && (
                <span className="text-xs text-gray-400">Loading…</span>
              )}
              <button
                type="button"
                onClick={onOpenTemplatePicker}
                disabled={templatePickerDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  templatePickerDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                } ${shouldAnimateTemplatePicker ? 'analytics-setup-template-picker-zoom' : ''}`}
              >
                Pick a template
              </button>
              <div className="relative group">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                >
                  Generate from Script
                </button>
                <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 whitespace-nowrap">
                  Coming soon
                </div>
              </div>
            </div>
          </div>
          <PromptTagReferenceLabel items={mainPromptTagReferences} />
          <PromptTagEditor
            value={loadingSetup ? 'Loading prompt…' : promptDraft}
            onChange={setPromptDraft}
            disabled={loadingSetup || !hasSelectedScript}
            knownTags={mainPromptKnownTags}
            knownTagPatterns={knownPromptTagPatterns}
            requiredTags={isSurveyType ? [questionsTag] : []}
            minHeightClassName="min-h-[380px]"
          />
        </div>

        {isSurveyType && (
          <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Questions</h3>
              <div className="flex items-center gap-2">
                {loadingSetup && (
                  <span className="text-xs text-gray-400">Loading…</span>
                )}
                <button
                  type="button"
                  onClick={onGenerateQuestions}
                  disabled={!hasSelectedScript || loadingSetup || generateState === 'generating'}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    !hasSelectedScript || loadingSetup || generateState === 'generating'
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {generateState === 'generating' ? 'Generating…' : 'Generate from Script'}
                </button>
              </div>
            </div>
            <textarea
              className="w-full min-h-[380px] resize-y rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-mono text-gray-800"
              value={loadingSetup ? 'Loading questions…' : questionsDraft}
              onChange={(event) => setQuestionsDraft(event.target.value)}
              disabled={loadingSetup || !hasSelectedScript}
            />
          </div>
        )}
        </div>
      )}
    </section>
  );
};

export default MainAnalysisConfigurator;
